import { NUMBER } from './GeneralConstants'
import { Device } from './catalogs/Devices'
import { GroupStatus } from './catalogs/Groups'
import { License } from './catalogs/Licenses'
import { CLIENT_REGEXPS, DOMAIN_REGEXP, GENERAL_REGEXPS } from './RegExpressions'
import { SortDirection } from './Support'

// Client ID in DB
export const BLUEMESSAGING_ID = 1
// Constants
export const UNDEFINED_ENTITY_ID = -1

export enum PROCESS_STATUS {
  INIT,
  SUCCESS,
  ERROR
}

export enum CrudActions {
  CREATE, READ, READ_DETAIL, UPDATE, ACTIVE, INACTIVE, SUSPEND, DELETE, WORKSPACES, DOMAINS, LICENSES
}

export class CatalogGridItem {
  id: number
}

export class CatalogGridItemColumn {
  key: string
  name: string
  sort: boolean
  dir = 'asc'

  private constructor (key: string, name: string, sort?: boolean) {
    this.key = key
    this.name = name
    this.sort = sort || false
  }

  static def (key: string, name: string, sort?: boolean) {
    return new CatalogGridItemColumn(key, name, sort)
  }
}

/** General */

export class Country {
  code: string
  name: string
}

export class BasicEntity {
  id: number
  name: string
  constructor (id: number, name: string) {
    this.id = id
    this.name = name
  }
}

/** Clients */

export class ClientCatalogGridItem extends CatalogGridItem {
  clientCode: string
  name: string
  status: string
  statusName: string
  rfc: string
  contactName: string
  contactEmail: string
  contactPhone: string
  contactEmailExtra: string
  contactPhoneExtra: string
  comercialCharge: string
  juridicalPerson: string
  minimumLicenses: number
  stpClabe: string
  adddressStreet: string
  addressExtNum: string
  addressIntNum: string
  addressDistrict: string
  addressDelegation: string
  addressState: string
  addressZipcode: string
  addressCountry: string
  startDate: string
  creation: string
  update: string
  verification: string
}

export enum ClientStatus {
  APROBACION = 'pending',
  ACTIVO = 'active',
  SUSPENDIDO = 'suspended',
  BAJA = 'inactive'
}

export enum JuridicalPerson {
  MORAL = 'moral',
  PHYSICAL = 'physical'
}

export class ClientCatalogFilters {
  status: string[] = []
  search: string = ''
}

export class Client {
  id: number
  clientCode: string
  name: string
  rfc: string
  status: string
  juridicalPerson: string
  stpClabe: string
  comercialCharge: string
  startDate: Date
  minimumLicenses: number
  address: {
    streetAddress: string
    extNumAddress: string
    intNumAddress: string
    district: string
    delegation: string
    state: string
    zipcode: string
    country: string
  }
  contact: {
    contactName: string
    email: string
    phone: string
    emailExtra: string
    phoneExtra: string
  }
  // Automatic fields
  creationDate: Date
  creationUser: {
    id: number,
    name: string
  }
  updateDate: Date
  updateUser: {
    id: number,
    name: string
  }
  verificationDate: Date
  verificationUser: {
    id: number,
    name: string
  }

  static getIntanceForEdit () {
    const instance = new Client()
    instance.name = ''
    instance.clientCode = ''
    instance.rfc = ''
    instance.juridicalPerson = JuridicalPerson.MORAL
    instance.stpClabe = ''
    instance.contact = {
      contactName: '',
      email: '',
      phone: '',
      emailExtra: '',
      phoneExtra: ''
    }
    instance.address = {
      streetAddress: '',
      extNumAddress: '',
      intNumAddress: '',
      country: 'México',
      district: '',
      delegation: '',
      state: '',
      zipcode: ''
    }
    instance.comercialCharge = ''
    instance.minimumLicenses = 0
    instance.startDate = '' as any

    return instance
  }

  static getStatusDescription (status: ClientStatus) {
    switch (status) {
      case ClientStatus.APROBACION:
        return 'En aprobación'
      case ClientStatus.ACTIVO:
        return 'Activo'
      case ClientStatus.SUSPENDIDO:
        return 'Suspendido'
      case ClientStatus.BAJA:
        return 'Baja'
      default:
        return ''
    }
  }

  /**
   * Validates if the instances has at least all required values
   * to request a POST method
   * @returns true if all required values are filled, false otherwise
   */
  validateRequiredValues () : boolean {
    return !!this.name &&
      !!this.clientCode &&
      !!this.rfc &&
      !!this.juridicalPerson &&
      !!this.contact.email &&
      !!this.contact.phone &&
      !!this.contact.contactName &&
      !!this.address.streetAddress &&
      !!this.address.extNumAddress &&
      !!this.address.district &&
      !!this.address.delegation &&
      !!this.address.state &&
      !!this.address.zipcode &&
      !!this.address.country
  }

  /** Validates client code format */
  validateClientCode () : boolean {
    return new RegExp(CLIENT_REGEXPS.regexClientCode).test(this.clientCode)
  }

  /** Validates client juridical person format */
  validateJuridicalPerson () : boolean {
    return Object.values(JuridicalPerson).includes(this.juridicalPerson as JuridicalPerson)
  }

  /** Validates client rfc format */
  validateRFC () : boolean {
    return this.juridicalPerson === JuridicalPerson.MORAL
      ? new RegExp(CLIENT_REGEXPS.regexRfcPersonaMoral).test(this.rfc)
      : new RegExp(CLIENT_REGEXPS.regexRfcPersonaFisica).test(this.rfc)
  }

  /** Validates client stp format */
  validateSTP () : boolean {
    return (!!this.stpClabe && new RegExp(CLIENT_REGEXPS.regexStpClabe).test(this.stpClabe)) || !this.stpClabe
  }

  /** Validates phone format */
  validatePhoneFormat (phone: string) : boolean {
    return new RegExp(CLIENT_REGEXPS.regexPhone).test(phone)
  }

  /** Validates client phone format */
  validatePhone () : boolean {
    return this.validatePhoneFormat(this.contact.phone)
  }

  /** Validates client extra phone format */
  validateExtraPhone () : boolean {
    return (!!this.contact.phoneExtra && this.validatePhoneFormat(this.contact.phoneExtra)) || !this.contact.phoneExtra
  }

  /** Validates client zip code format */
  validateZipCode () : boolean {
    return new RegExp(CLIENT_REGEXPS.regexZipCode).test(this.address.zipcode)
  }

  /** Validates client comercial charge format */
  validateComercialCharge () : boolean {
    return (!!this.comercialCharge && new RegExp(CLIENT_REGEXPS.regexComercialCharge).test(this.comercialCharge)) || !this.comercialCharge
  }

  /** Validates client minumun licenses format */
  validateMinimunLicenses () : boolean {
    return (!!this.minimumLicenses && new RegExp(CLIENT_REGEXPS.regexMinLicenses).test(this.minimumLicenses.toString())) || !this.minimumLicenses
  }

  /** Returns a list of the invalid property formats in the client */
  invalidFormatValues () : string[] {
    const errorFormats: string[] = []
    if (!this.validateClientCode()) errorFormats.push(`Codigo de cliente (client_code: ${this.clientCode})`)
    if (!this.validateRFC()) errorFormats.push(`RFC (rfc: ${this.rfc})`)
    if (!this.validateSTP()) errorFormats.push(`STP (stp_clabe: ${this.stpClabe}; 18 caracteres)`)
    if (!this.validateJuridicalPerson()) errorFormats.push(`Persona Jurídica (juridical_person: ${this.juridicalPerson}; moral o physical)`)
    if (!this.validatePhone()) errorFormats.push(`Teléfono (phone: ${this.contact.phone})`)
    if (!this.validateExtraPhone()) errorFormats.push(`Teléfono extra (phone_extra: ${this.contact.phoneExtra})`)
    if (!this.validateZipCode()) errorFormats.push(`Código postal (zip_code: ${this.address.zipcode})`)
    if (!this.validateComercialCharge()) errorFormats.push(`Cargo comercial (comercial_charge: ${this.comercialCharge}; 4 dígitos)`)
    if (!this.validateMinimunLicenses()) errorFormats.push(`Mínimo de licencias (minimun_licenses: ${this.minimumLicenses})`)
    return errorFormats
  }
}

export class ClientWorkspacesFilters {
  clientId: number
  domainId: number
}

export enum WorkspacesAction {
  ACTIVATION = 'Activación',
  SUSPENSION = 'Suspensión',
  INACTIVATION = 'Baja'
}

export enum WorkspaceStatus {
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  INACTIVE = 'inactive'
}

export class Workspace {
  id: number
  owner: boolean
  status: WorkspaceStatus
  facturationDate: number
  consolidatedInvoice: boolean
  contact: {
    name: string
    contactTelephone: string
    contactEmail: string
  }
  domain: {
    id: number
    name?: string
  }
  client: {
    id: number
    name?: string
    clientCode?: string
    status?: ClientStatus
  }
  updateUser: {
    id: number
    name: string
  }
  creationDate: Date
  creationUser: {
    id: number
    name: string
  }
  _selected: boolean

  static getIntanceForEdit () {
    const instance = new Workspace()
    instance.owner = false
    instance.status = WorkspaceStatus.ACTIVE
    instance.facturationDate = 1
    instance.consolidatedInvoice = false
    instance.contact = {
      name: '',
      contactTelephone: '',
      contactEmail: ''
    }
    instance.domain = {
      id: 0
    }
    instance.client = {
      id: 0
    }

    return instance
  }

  static getStatusDescription (status: string) {
    switch (status) {
      case 'active':
        return 'Activo'
      case 'suspended':
        return 'Suspendido'
      case 'inactive':
        return 'Baja'
      default:
        return ''
    }
  }

  /** Validates if the workspace entity has all required values */
  validateRequiredValues () : boolean {
    return (this.validateBooleanValue(this.owner) || !!this.owner) &&
      !!this.facturationDate &&
      (this.validateBooleanValue(this.consolidatedInvoice) || !!this.consolidatedInvoice) &&
      !!this.contact.name &&
      !!this.contact.contactTelephone &&
      !!this.contact.contactEmail &&
      this.domain.id > -1 &&
      this.client.id > 0
  }

  /** Validates if the value is a boolean type */
  validateBooleanValue (value: any) : boolean {
    return (value === 0 || value === 1) ||
      (value === 'true' || value === 'false')
  }

  /** Validates if the owner property is a boolean type */
  validateOwnerValue () : boolean {
    return this.validateBooleanValue(this.owner)
  }

  /** Validates if the owner consolidatedInvoice is a boolean type */
  validateConsolidatedInvoice () : boolean {
    return this.validateBooleanValue(this.consolidatedInvoice)
  }

  /** Validate if the email is a valid format */
  validateEmail () : boolean {
    return new RegExp(GENERAL_REGEXPS.email).test(this.contact.contactEmail)
  }

  /** Validates if the phone is a valid format */
  validatePhoneNumber () : boolean {
    return new RegExp(CLIENT_REGEXPS.regexPhone).test(this.contact.contactTelephone)
  }

  /** Validates if the workspace entity has valid format values */
  validateFormatValues () : string[] {
    const formatErrors: string[] = []
    if (!this.validateOwnerValue()) formatErrors.push(`Es propiertario (Owner: ${this.owner}; 0, 1, true o false)`)
    if (!this.validateConsolidatedInvoice()) formatErrors.push(`Consolidación de factura (ConsolidatedInvoice: ${this.consolidatedInvoice}; 0, 1, true o false)`)
    if (!this.validateEmail()) formatErrors.push(`Correo electrónico (ContacEmail: ${this.contact.contactEmail})`)
    if (!this.validatePhoneNumber()) formatErrors.push(`Teléfono (ContactPhone: ${this.contact.contactTelephone})`)
    return formatErrors
  }
}

/** Domains */

export enum DomainSecurityLevel {
  LOW = 0,
  MEDIUM = 4,
  HIGH = 8
}

export enum MapProvider {
  MAPBOX = 'MAPBOX',
  GOOGLE = 'GOOGLE'
}

export enum FullTextSearchType {
  ELASTIC = 'ELASTIC_SEARCH',
  SOLR = 'SOLR'
}

export enum TrackingMode {
  PASIVO = 'pasivo',
  INTENSIVO = 'intensivo'
}

export enum DomainType {
  ECOSYSTEM = 'ecosystem',
  INDIVIDUAL = 'individual'
}

export enum DomainStatusLabel {
  OPEN = 'Abierto',
  CLOSED = 'Cerrado'
}

export class Domain {
  id: number
  name: string
  securityLevel: number
  minimumRevision: number
  webBMFMinimumRevision: number
  broadcast: string
  usePredictiveDialerOpt: boolean
  limitLicenses: boolean
  mapProvider: string
  fullTextSearchType: string
  rateLimitTime: number
  rateLimitWS: number
  trackingMode: string
  trackerVersion: number = NUMBER.TWO
  productLine: string
  businessSegment: string
  domainType: string
  billingCode: string
  creationDate: Date
  creationUser: {
    id: number,
    name: string
  }
  updateDate: Date
  updateUser: {
    id: number,
    name: string
  }
  closed: boolean

  static getIntanceForEdit () {
    const instance = new Domain()
    instance.name = ''
    instance.securityLevel = DomainSecurityLevel.HIGH
    instance.minimumRevision = 0.0
    instance.webBMFMinimumRevision = 0.0
    instance.broadcast = ''
    instance.usePredictiveDialerOpt = false
    instance.limitLicenses = true
    instance.mapProvider = MapProvider.MAPBOX
    instance.fullTextSearchType = FullTextSearchType.ELASTIC
    instance.rateLimitTime = '' as any
    instance.rateLimitWS = '' as any
    instance.trackerVersion = NUMBER.TWO
    instance.trackingMode = '' as any
    instance.productLine = 'Smartforms'
    instance.businessSegment = ''
    instance.domainType = DomainType.ECOSYSTEM
    instance.billingCode = ''
    instance.closed = false

    return instance
  }

  static getDomainTypeDescription (domainType: string) {
    switch (domainType) {
      case 'ecosystem':
        return 'Ecosistema'
      case 'individual':
        return 'Cliente individual'
      default:
        return ''
    }
  }

  static getDomainFullTextSearchTypeDescription (searchType: string) {
    switch (searchType) {
      case 'ELASTIC_SEARCH':
        return 'Elastic Search'
      default:
        return ''
    }
  }

  static getDomainSecurityLevelDescription (searchType: any) {
    searchType = searchType.toString()
    switch (searchType) {
      case '0':
        return 'Bajo'
      case '4':
        return 'Medio'
      case '8':
        return 'Alto'
      default:
        return ''
    }
  }

  getDomainStatusLabel () : string {
    return this.closed ? DomainStatusLabel.CLOSED : DomainStatusLabel.OPEN
  }

  /**
   * @returns true if the domain is of type INDIVIDUAL, otherwise false
   */
  isIndividual (): boolean {
    return this.domainType === DomainType.INDIVIDUAL
  }

  /**
   * Validates if the entity has all required values
   * @returns a list of missing required values
   */
  validateRequiredValues () : string[] {
    const requiredValues: string[] = []
    if (this.name === null || this.name === undefined) requiredValues.push('name')
    if (this.securityLevel === null || this.securityLevel === undefined) requiredValues.push('securityLevel')
    if (this.webBMFMinimumRevision === null || this.webBMFMinimumRevision === undefined) requiredValues.push('webBMFMinimumRevision')
    if (this.usePredictiveDialerOpt === null || this.usePredictiveDialerOpt === undefined) requiredValues.push('usePredictiveDialerOpt')
    if (this.limitLicenses === null || this.limitLicenses === undefined) requiredValues.push('limitLicenses')
    if (this.mapProvider === null || this.mapProvider === undefined) requiredValues.push('mapProvider')
    if (this.fullTextSearchType === null || this.fullTextSearchType === undefined) requiredValues.push('fullTextSearchType')
    if (this.rateLimitTime === null || this.rateLimitTime === undefined) requiredValues.push('rateLimitTime')
    if (this.rateLimitWS === null || this.rateLimitWS === undefined) requiredValues.push('rateLimitWS')
    if (this.productLine === null || this.productLine === undefined) requiredValues.push('productLine')
    if (this.businessSegment === null || this.businessSegment === undefined) requiredValues.push('businessSegment')
    if (this.domainType === null || this.domainType === undefined) requiredValues.push('domainType')
    if (this.billingCode === null || this.billingCode === undefined) requiredValues.push('billingCode')
    return requiredValues
  }

  /** Validates if the entity has a name */
  validateName () : boolean {
    return !!this.name
  }

  /** Validates if the entity has a security level */
  validateSecurityLevel () : boolean {
    return Object.values(DomainSecurityLevel).includes(this.securityLevel)
  }

  /** Validates if the entity has a web bmf minimun revision */
  validateWebBMFMinimunRevision () : boolean {
    return this.webBMFMinimumRevision >= 0
  }

  /** Validates if a 'value' is boolean type 0, 1, false, true */
  validateBooleanValue (value: any) : boolean {
    return value === 0 || value === 1 || value === 'true' || value === 'false' || value === false || value === true
  }

  /** Validates if the entity has a usePredictiveDialerOpt valid */
  validateUserPredictiveDialerOpt () : boolean {
    return this.validateBooleanValue(this.usePredictiveDialerOpt)
  }

  /** Validates if the entity has a limitLicenses valid */
  validateLimitLicenses () : boolean {
    return this.validateBooleanValue(this.limitLicenses)
  }

  /** Validates if the entity has a mapProvider valid */
  validateMapProvider () : boolean {
    return Object.values(MapProvider).includes(this.mapProvider as MapProvider)
  }

  /** Validates if the entity has a fullTextSearchType valid */
  validateFullTextSearchType () : boolean {
    return Object.values(FullTextSearchType).includes(this.fullTextSearchType as FullTextSearchType)
  }

  /** Validates if the entity has a rateLimitTime valid */
  validateRateLimitTime () : boolean {
    return this.rateLimitTime >= -1
  }

  /** Validates if the entity has a rateLimitWS valid */
  validateRateLimitWS () : boolean {
    return this.rateLimitWS >= -1
  }

  /** Validates if the entity has a trackingMode valid */
  validateTrackingMode () : boolean {
    return this.trackingMode ? Object.values(TrackingMode).includes(this.trackingMode as TrackingMode) : (this.trackingMode === null || this.trackingMode === undefined)
  }

  /** Validates if the entity has a businessSegment valid */
  validateBussinesSegment () : boolean {
    return new RegExp(DOMAIN_REGEXP.regexpBussinesSegment).test(this.businessSegment)
  }

  /** Validates if the entity has a domainType valid */
  validateDomainType () : boolean {
    return Object.values(DomainType).includes(this.domainType as DomainType)
  }

  /** Validates if the entity has a regexpBillingCode valid */
  validateBillingCode () : boolean {
    return new RegExp(DOMAIN_REGEXP.regexpBillingCode).test(this.billingCode)
  }

  /**
   * Validates the format properties from the entity
   * @returns a list of format invalid
   */
  validateFormatValues () : string[] {
    const formatErrors: string[] = []
    if (!this.validateName()) formatErrors.push(`Nombre (name: ${this.name})`)
    if (!this.validateSecurityLevel()) formatErrors.push(`Nivel de seguridad (securityLevel: ${this.securityLevel}; 0, 4, 8)`)
    if (!this.validateWebBMFMinimunRevision()) formatErrors.push(`Revisión mínima de Web BMF (webBMFMinimumRevision: ${this.webBMFMinimumRevision})`)
    if (!this.validateUserPredictiveDialerOpt()) formatErrors.push(`Marcado predictivo (usePredictiveDialerOpt: ${this.usePredictiveDialerOpt}; 0, 1, false o true)`)
    if (!this.validateLimitLicenses()) formatErrors.push(`Límite de licencias (limitLicenses: ${this.limitLicenses}; 0, 1, false o true)`)
    if (!this.validateMapProvider()) formatErrors.push(`Proveedor de mapa (mapProvider: ${this.mapProvider}; ${Object.values(MapProvider)})`)
    if (!this.validateFullTextSearchType()) formatErrors.push(`Tipo de búsqueda por texto (fullTextSearchType: ${this.fullTextSearchType}; ${Object.values(FullTextSearchType)})`)
    if (!this.validateRateLimitTime()) formatErrors.push(`Tasa de tiempo límite (rateLimitTime: ${this.rateLimitTime})`)
    if (!this.validateRateLimitWS()) formatErrors.push(`Tasa límite WS (rateLimitWS: ${this.rateLimitWS})`)
    if (!this.validateTrackingMode()) formatErrors.push(`Modo de seguimiento (trackingMode: ${this.trackingMode}; pasivo, intensivo)`)
    if (!this.validateBussinesSegment()) formatErrors.push(`Segmento de negocio (businessSegment: ${this.businessSegment}; tres caracteres)`)
    if (!this.validateDomainType()) formatErrors.push(`Tipo de dominio (domainType: ${this.domainType}; ${Object.values(DomainType)})`)
    if (!this.validateBillingCode()) formatErrors.push(`Código de facturación (billingCode: ${this.billingCode}; 10 caracteres)`)
    return formatErrors
  }
}

export class DomainCatalogGridItem extends Domain {
  creationDateString: string
  updateDateString: string
  ownerDomain: string
  status: string
  domainTypeName: string
  securityLevelName: string
  firebaseUUID?: string
}

export class DomainCatalogFilters {
  domainType: string[] = []
  search: string = ''
  creation: any = ''
  loadRelations = false
}
/** Roles */
export class RoleCatalogGridItem extends CatalogGridItem {
  name: string
  copyFromRole: string
  maxSessions: number
  domain: number
  permissions: BasicEntity[]
  creation: string
  creationUser: string
  update: string
  updateUser: string
}

class BasicRole {
  id: number
  name: string
  copyFromRole: string
  copyFromRoleId: number
  maxSessions: number
  domain: number
  // Automatic fields
  creationDate: Date
  creationUser: {
    id: number,
    name: string
  }
  updateDate: Date
  updateUser: {
    id: number,
    name: string
  }
}

export class Role extends BasicRole {
  permissions: BasicEntity[]

  static getIntanceForEdit () {
    const instance = new Role()
    instance.name = ''
    instance.copyFromRole = ''
    instance.maxSessions = 0
    instance.domain = -1
    instance.permissions = []

    return instance
  }
}

export class RoleUpdate extends BasicRole {
  permissions: number[]

  static getIntanceForEdit () {
    const instance = new RoleUpdate()
    instance.name = ''
    instance.copyFromRole = ''
    instance.maxSessions = -1
    instance.domain = 0
    instance.permissions = []

    return instance
  }
}

export class RoleCatalogFilters {
  search: string = ''
  domain: number
}

/** Permissions */
export class PermissionCatalogGridItem extends CatalogGridItem {
  name: string
  domain: number
  resource: string
  effect: string
  label: string
  version: number
  description: string
  creation: string
  creationUser: string
  update: string
  updateUser: string
}

class BasicPermission {
  id: number
  name: string
  domain: number
  resource: string
  effect: string
  label: string
  version: number
  description: string
  // Automatic fields
  creationDate: Date
  creationUser: {
    id: number,
    name: string
  }
  updateDate: Date
  updateUser: {
    id: number,
    name: string
  }
}

export class Permission extends BasicPermission {
  roles: BasicEntity[]

  static getIntanceForEdit () {
    const instance = new Permission()
    instance.name = ''
    instance.domain = -1
    instance.resource = ''
    instance.effect = ''
    instance.label = ''
    instance.version = -1
    instance.description = ''
    instance.roles = []
    return instance
  }
}

export class PermissionUpdate extends BasicPermission {
  roles: number[]

  static getIntanceForEdit () {
    const instance = new PermissionUpdate()
    instance.name = ''
    instance.domain = 0
    instance.resource = ''
    instance.effect = ''
    instance.label = ''
    instance.version = -1
    instance.description = ''
    instance.roles = []

    return instance
  }
}

export class PermissionCatalogFilters {
  search: string = ''
  domain: number
  resource: string = ''
  loadRelations: boolean = false
  versionFilters = [
    { text: 'Version 1', value: 1 },
    { text: 'Version 2', value: 2 }
  ]
  effectFilters = [
    { text: 'Create', value: 'create' },
    { text: 'Read', value: 'read' },
    { text: 'Update', value: 'update' },
    { text: 'Delete', value: 'delete' },
    { text: 'Allow', value: 'allow' },
    { text: 'Deny', value: 'deny' },
    { text: 'Integrate', value: 'integrate' },
    { text: 'IntegrateFNL', value: 'integrateFNL' },
    { text: 'IntegrateWorked', value: 'integrateWorked' },
    { text: 'Assignment', value: 'assignment' }
  ]
  resourceFilters = [
    { text: 'Users', value: 'users' },
    { text: 'Groups', value: 'groups' },
    { text: 'Permissions', value: 'permissions' },
    { text: 'Roles', value: 'roles' },
    { text: 'Projects', value: 'projects' },
    { text: 'Clients', value: 'clients' },
    { text: 'Domains', value: 'domains' },
    { text: 'Workspaces', value: 'workspaces' },
    { text: 'Templates', value: 'templates' },
    { text: 'Fills', value: 'fills' },
    { text: 'Catalogs', value: 'catalogs' },
    { text: 'Attachments', value: 'attachments' },
    { text: 'Locations', value: 'locations' },
    { text: 'Datafeed', value: 'datafeed' },
    { text: 'Tasks', value: 'tasks' },
    { text: 'Integration', value: 'integration' }
  ]
  versionSelected: number[] = []
  effectSelected: string[] = []
  resourceSelected: string[] = []
  defaultSearchOptions: string = 'Name'
  columnOptions: string[] = [ 'Name', 'Domain', 'Resource', 'Effect', 'Label' ]
}

export class PermissionRoleFilters {
  basicFields = [
    { key: 'id', label: 'ID', sortable: true, thClass: 'smallColumnWidth' },
    { key: 'name', label: 'Nombre', sortable: true },
    { key: 'actions', label: 'Acciones', thClass: 'smallColumnWidth' }
  ]
  fieldsPermission = [
    { key: 'id', label: 'ID', sortable: true, thClass: 'smallColumnWidth' },
    { key: 'name', label: 'Nombre', sortable: true, thClass: 'mediumColumnWidth', tdClass: 'columnOverflow' },
    { key: 'resource', label: 'Recurso', sortable: true, tdClass: 'columnOverflow' },
    { key: 'effect', label: 'Efecto', sortable: true, thClass: 'smallColumnWidth', tdClass: 'columnOverflow' },
    { key: 'actions', label: 'Acciones', thClass: 'smallColumnWidth' }
  ]
  allRoles: RoleCatalogGridItem[] = []
  roleIds: number[] = []
  allPermissions: PermissionCatalogGridItem[] = []
  permissionIds: number[] = []
  perPage = 10
  currentPage = 1
  totalRoles = 0
  totalPermissions = 0
}

/** Usuarios */

export class UserCatalogGridItem extends CatalogGridItem {
  domain: string
  client: string
  roleName: string
  name: string
  firstName: string
  lastName: string
  rfc: string
  email: string
  cellPhone: string
  status: string
  statusName: string
  groupsNames: string
  updateUser: string
  updateDate: string
  creationDateString: string
  updateDateString: string
  disableDateString: string
  billable: string
  extendedPermissions: string
  billableString: string
  extendedPermissionsString: string
  activeLicenses: License[] = []
  activeDevices: Device[] = []
  _selected: boolean
}

export enum UserStatus {
  ACTIVO = 'active',
  INACTIVO = 'inactive',
  SUSPENDED = 'suspended',
  UPDATE_PASSWORD = 'updatePassword'
}

export class UserCatalogFilters {
  name: string
  search: string
  domainId: number
  clientId: number
  groupId: number
  subGroup: boolean = false
  roleId: number
  copyFromRole: boolean = false
  status: string[] = []
  simpleStatus: string = ''
  licenseType: string = ''
  billable = false
  date: Date
  fromTo: Date[] = []
  loadRelations: boolean
  order: string = 'name'
  sortOrder: SortDirection = SortDirection.ASC
}

export class User {
  id: number
  name: string
  firstName: string
  lastName: string
  email: string
  emailExtra: string
  status: UserStatus
  billable: boolean
  extendedPermissions: boolean
  client: BasicEntity
  domain: BasicEntity
  role: BasicEntity
  groups: BasicEntity[] = []
  phoneExtra: string
  cellPhone: {
    countryCode: string
    number?: string
  }
  // Automatic fields
  creationDate: Date
  creationUser: {
    id: number,
    name: string
  }
  updateDate: Date
  updateUser: {
    id: number,
    name: string
  }
  disableDate: Date
  resetPassword: boolean

  static getIntanceForEdit () {
    const instance = new User()
    instance.status = UserStatus.ACTIVO
    instance.name = ''
    instance.firstName = ''
    instance.lastName = ''
    instance.billable = true
    instance.extendedPermissions = true
    instance.cellPhone = {
      countryCode: '+52',
      number: undefined
    }
    instance.client = new BasicEntity(0, '')
    instance.domain = new BasicEntity(0, '')
    instance.role = new BasicEntity(0, '')
    instance.groups = []
    instance.resetPassword = false

    return instance
  }

  static getStatusDescription (status: string) {
    switch (status) {
      case 'active':
        return 'Activo'
      case 'inactive':
        return 'Baja'
      case 'suspended':
        return 'Suspendido'
      case 'updatePassword':
        return 'Contraseña restablecida'
      default:
        return ''
    }
  }

  /**
   * Validates if the entity has all required values
   * @returns a list of missing values required
   */
  validateRequiredValues () : string[] {
    const missingValues: string[] = []
    if (!this.name) missingValues.push('name')
    if (!this.email) missingValues.push('email')
    if (this.billable === null || this.billable === undefined) missingValues.push('billable')
    if (this.extendedPermissions === null || this.extendedPermissions === undefined) missingValues.push('extendedPermissions')
    if (!this.client || !this.client.hasOwnProperty('id') || !this.client.id) missingValues.push('client')
    if (!(this.domain.id >= 0)) missingValues.push('domain')
    if (!this.role.id) missingValues.push('role')
    return missingValues
  }

  /** Validates an email valid format */
  validateEmailFormat (email: string) : boolean {
    return new RegExp(GENERAL_REGEXPS.email).test(email)
  }

  /** Validates if the entity has an email valid */
  validateEmail () : boolean {
    return this.validateEmailFormat(this.email)
  }

  /** Validates if the entity has a valid extraEmail */
  validateEmailExtra () : boolean {
    return this.emailExtra ? this.validateEmailFormat(this.emailExtra) : true
  }

  /** Validates the phone format */
  validatePhoneFormat (phone: string) {
    return new RegExp(CLIENT_REGEXPS.regexPhone).test(phone)
  }

  /** Validates if the entity has a valid cell phone */
  validateCellPhone () : boolean {
    return this.cellPhone.countryCode && this.cellPhone.number
      ? this.validatePhoneFormat(this.cellPhone.countryCode + this.cellPhone.number)
      : true
  }

  /** Validate if the entity has a valid extra phone */
  validatePhoneExtra () : boolean {
    return this.phoneExtra ? this.validatePhoneFormat(this.phoneExtra) : true
  }

  /**
   * Validates if the value is a boolean value
   * @param value - to check
   * @returns - true if is a boolean value, false otherwise
   */
  validateBooleanFormat (value: any) : boolean {
    return value === 0 || value === 1 || value === true || value === false || value === 'true' || value === 'false'
  }

  /** Validate if the entity has a valid billable value */
  validateBillable () : boolean {
    return this.validateBooleanFormat(this.billable)
  }

  /** Validate if the entity has a valid extendedPermissions value */
  validateExtendedPermissions () : boolean {
    return this.validateBooleanFormat(this.extendedPermissions)
  }

  validateClient () : boolean {
    return this.client.id > 0
  }

  validateDomain () : boolean {
    return this.domain.id >= 0
  }

  validateRole () : boolean {
    return this.role.id > 0
  }

  /**
   * Validates if the value formats are valid
   * @returns a list of invalid values
   */
  validateFormatValues () : string[] {
    const invalidValues: string[] = []
    if (!this.name) invalidValues.push(`Nombre (name: ${this.name})`)
    if (!this.validateEmail()) invalidValues.push(`Correo (email: ${this.email})`)
    if (!this.validateEmailExtra()) invalidValues.push(`Correo extra (emailExtra: ${this.email})`)
    if (!this.validateCellPhone()) invalidValues.push(`Teléfono (cellPhone: ${this.cellPhone.countryCode + this.cellPhone.number})`)
    if (!this.validatePhoneExtra()) invalidValues.push(`Teléfono extra (phoneExtra: ${this.phoneExtra})`)
    if (!this.validateBillable()) invalidValues.push(`Facturable (billable ${this.billable}; 0, 1, false, true)`)
    if (!this.validateExtendedPermissions()) invalidValues.push(`Permisos extendidos (extendedPermissions ${this.extendedPermissions}; 0, 1, false, true)`)
    if (!this.validateClient()) invalidValues.push(`Cliente (clientId: ${this.client.id})`)
    if (!this.validateDomain()) invalidValues.push(`Dominio (domainId: ${this.domain.id})`)
    if (!this.validateRole()) invalidValues.push(`Rol (roleId: ${this.role.id})`)
    return invalidValues
  }
}

/** Grupos */

export class Group {
  id: number
  name: string
  subGroups: Group[]
  status: GroupStatus
  _selected = false
}
